/**
 * Shows "Added to cart" notification on successful add (quickshop or product page).
 * Listens for ThemeEvents.cartUpdate, fetches cart, displays last added item.
 */
import { ThemeEvents } from "@theme/events";

const NOTIFICATION_ID = "added-to-cart-notification";
const HIDE_ANIMATION_MS = 250;

function getCartUrl() {
  const el = document.getElementById(NOTIFICATION_ID);
  return el?.getAttribute("data-cart-url") || "/cart";
}

function getEl(selector) {
  const root = document.getElementById(NOTIFICATION_ID);
  return root?.querySelector(selector) ?? null;
}

/** Largest rendered dimension of the thumbnail (72x96 box). */
const IMAGE_RENDER_PX = 96;

/**
 * cart.js returns the full-size product image URL, which can be 1500-2048px
 * wide and weigh hundreds of KB. Dropping that into a 72x96 box means the
 * browser downloads the whole thing before painting (the 2-3s delay). Ask the
 * Shopify CDN for a thumbnail scaled to the rendered size (capped at 3x DPR).
 * @param {string} url
 * @returns {string}
 */
function notificationImageUrl(url) {
  if (!url) return "";
  const dpr = Math.min(window.devicePixelRatio || 1, 3);
  const width = Math.round(IMAGE_RENDER_PX * dpr);
  try {
    const parsed = new URL(url, window.location.origin);
    parsed.searchParams.set("width", String(width));
    return parsed.toString();
  } catch {
    return url;
  }
}

function formatMoney(cents, currency) {
  const code = currency || window.Shopify?.currency?.active || "SEK";
  const locale =
    window.Shopify?.locale || document.documentElement.lang || "en";
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: code,
    }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${code}`;
  }
}

/**
 * Clears the dynamic content so the next add starts blank. Without this, a
 * re-trigger briefly shows the previously added product's image/title/price
 * until the new cart.js fetch resolves — better to show nothing than the
 * wrong item.
 */
function clearNotificationContent() {
  const imageEl = getEl("[data-notification-image]");
  const titleEl = getEl("[data-notification-title]");
  const priceEl = getEl("[data-notification-price]");
  const announcementEl = document.querySelector(
    "[data-notification-announcement]",
  );
  if (imageEl) {
    imageEl.removeAttribute("src");
    imageEl.alt = "";
  }
  if (titleEl) titleEl.innerHTML = "";
  if (priceEl) priceEl.textContent = "";
  if (announcementEl) announcementEl.textContent = "";
}

/** @param {HTMLElement} el */
function focusWithoutScroll(el) {
  try {
    el.focus({ preventScroll: true });
  } catch {
    el.focus();
  }
}

/**
 * @type {HTMLElement | null}
 */
let returnFocusTo = null;

let lastInputWasKeyboard = false;

/**
 * The add-to-cart control the user last pressed.
 * @type {HTMLElement | null}
 */
let lastAddTrigger = null;

/** @param {Event} event */
function rememberAddTrigger(event) {
  const target = event.target;
  const trigger =
    target instanceof Element ? target.closest('button[name="add"]') : null;
  if (trigger instanceof HTMLElement) lastAddTrigger = trigger;
}

/** @param {Element | null} el */
function usableTarget(el) {
  return el instanceof HTMLElement && el !== document.body && el.isConnected
    ? el
    : null;
}

/** Pending end-of-slide-out timer, so a re-show can cancel it. */
let hideFinishTimeoutId = null;

/**
 * @param {{ restoreFocus?: boolean }} [options]
 */
function hideNotification({ restoreFocus = false } = {}) {
  const el = document.getElementById(NOTIFICATION_ID);
  if (!el) return;
  el.classList.add("added-to-cart-notification--hiding");

  if (restoreFocus) {
    const target = usableTarget(returnFocusTo) ?? usableTarget(lastAddTrigger);
    if (target) focusWithoutScroll(target);
    else if (el.contains(document.activeElement)) el.blur();
  }
  returnFocusTo = null;

  clearTimeout(hideFinishTimeoutId);
  hideFinishTimeoutId = setTimeout(() => {
    el.setAttribute("aria-hidden", "true");
    el.classList.remove("added-to-cart-notification--hiding");
    clearNotificationContent();
  }, HIDE_ANIMATION_MS);
}

/** Track cart item count so we only show on add, not on remove */
let previousItemCount = 0;

function announceStatus() {
  const announcementEl = document.querySelector(
    "[data-notification-announcement]",
  );
  if (!announcementEl) return;
  const heading = getEl("#added-to-cart-notification-title")
    ?.textContent?.trim()
    .replace(/[.:!]+$/, "");
  const product = getEl("[data-notification-title]")?.textContent?.trim();
  announcementEl.textContent = [heading, product].filter(Boolean).join(": ");
}

/** @param {HTMLElement} el */
function focusNotification(el) {
  if (el.contains(document.activeElement)) return;

  returnFocusTo =
    usableTarget(document.activeElement) ?? usableTarget(lastAddTrigger);

  focusWithoutScroll(el);
}

function showNotification() {
  const el = document.getElementById(NOTIFICATION_ID);
  if (!el) return;

  // A hide may still be sliding out; its timer would blank the panel we are
  // about to show.
  clearTimeout(hideFinishTimeoutId);
  hideFinishTimeoutId = null;
  el.classList.remove("added-to-cart-notification--hiding");
  el.setAttribute("aria-hidden", "false");

  // Pointer users keep their place on the page, so the panel announces itself
  // instead of taking focus. Keyboard users get focus moved in.
  if (!lastInputWasKeyboard) {
    announceStatus();
    return;
  }

  const closingDialog = document.activeElement?.closest?.("dialog[open]");
  if (closingDialog) {
    closingDialog.addEventListener(
      "close",
      () => requestAnimationFrame(() => focusNotification(el)),
      { once: true },
    );
    return;
  }

  // One frame so the panel has left visibility:hidden before we focus it.
  requestAnimationFrame(() => focusNotification(el));
}

async function handleCartUpdate(event) {
  const data = event?.detail?.data;
  if (data?.didError) return;

  const el = document.getElementById(NOTIFICATION_ID);
  if (!el) return;

  /*
   * Changes made from inside the cart itself — the upsell toggle, and the
   * line quantity steppers — already show their result where the user is
   * standing, on a line the cart announces anyway; a toast over the top of
   * that is the same news twice, and on the keyboard path it pulls focus out
   * of the stepper mid-edit.
   *
   * Handled here rather than by not listening, because the count below
   * still has to be kept: skip it and the next real add is measured against
   * a stale number, which is enough to make a later removal read as an add.
   */
  const silent =
    data?.source === "cart-upsell-toggle" ||
    data?.source === "cart-items-component";

  // If the toast is still up from a previous add, wipe it now so the old
  // product isn't shown during the cart.js fetch (~0.5s). Blank is better
  // than the wrong item.
  if (!silent && el.getAttribute("aria-hidden") === "false") {
    clearNotificationContent();
  }

  try {
    const res = await fetch(`${getCartUrl()}.js`);
    if (!res.ok) return;
    const cart = await res.json();

    const itemCount = cart.item_count ?? 0;
    const wasAdd = itemCount > previousItemCount;
    previousItemCount = itemCount;

    if (!wasAdd || silent) return;

    const imageEl = getEl("[data-notification-image]");
    const titleEl = getEl("[data-notification-title]");
    const priceEl = getEl("[data-notification-price]");

    const items = cart.items ?? [];
    const variantId = event?.detail?.sourceId;
    const addedItem =
      (variantId &&
        items.find((item) => String(item.variant_id) === String(variantId))) ||
      items[items.length - 1];
    if (addedItem) {
      if (imageEl) {
        // Decorative: the product title sits next to it in the same panel, and
        // an alt here made screen readers read the name twice.
        imageEl.src = notificationImageUrl(addedItem.image);
      }
      if (titleEl) titleEl.innerHTML = addedItem.title ?? "";
      if (priceEl) {
        const price =
          addedItem.final_line_price ??
          addedItem.line_price ??
          addedItem.price * (addedItem.quantity ?? 1);
        priceEl.textContent = formatMoney(price, cart.currency ?? "SEK");
      }
    }

    showNotification();
  } catch {
    const fallbackCount = event?.detail?.cart?.item_count;
    if (typeof fallbackCount === "number") {
      previousItemCount = fallbackCount;
    }
  }
}

document.addEventListener(ThemeEvents.cartUpdate, handleCartUpdate);

for (const type of ["pointerdown", "keydown", "click"]) {
  document.addEventListener(type, rememberAddTrigger, { capture: true });
}

document.addEventListener(
  "keydown",
  () => {
    lastInputWasKeyboard = true;
  },
  { capture: true },
);
document.addEventListener(
  "pointerdown",
  () => {
    lastInputWasKeyboard = false;
  },
  { capture: true },
);

// Initialize previous count so we only show when count increases (add), not when it decreases (remove)
fetch(`${getCartUrl()}.js`)
  .then((res) => (res.ok ? res.json() : null))
  .then((cart) => {
    if (cart) previousItemCount = cart.item_count ?? 0;
  })
  .catch(() => {});

const notificationEl = document.getElementById(NOTIFICATION_ID);

notificationEl?.addEventListener("click", (e) => {
  // The cart drawer and checkout take focus from here themselves, so hide
  // without restoring — putting focus back on the card button would fight them.
  const target = e.target?.closest?.(
    'a[href*="cart"], a[href*="checkout"], .added-to-cart-notification__view-cart',
  );
  if (target) hideNotification();
});

notificationEl?.addEventListener("keydown", (e) => {
  if (e.key !== "Escape") return;
  e.preventDefault();
  hideNotification({ restoreFocus: true });
});

// The panel never closes on a timer (Ally-28), so a click outside it is the
// close affordance for anyone not using the cross or the links inside.
document.addEventListener("click", (e) => {
  const el = document.getElementById(NOTIFICATION_ID);
  if (!el || el.getAttribute("aria-hidden") !== "false") return;
  if (e.target instanceof Node && el.contains(e.target)) return;
  // No restoreFocus: the user is already acting somewhere else on the page.
  hideNotification();
});
